import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { toast } from "sonner";

import { Card } from "~/components/ui/card";
import { ConfirmationDialog } from "~/components/ui/confirmation-dialog";
import { Switch } from "~/components/ui/switch";
import { useUnlockedKey, useShareWithCoaches } from "~/lib/crypto";
import { useCategoriesOfLife } from "~/lib/queries/categoryQuery";
import {
  useShareOverallViewWithCoach,
  useUserSharedStatus,
} from "~/lib/queries/userSharedQuery";

import { BucketList } from "./-components/bucket-list";
import { CategoriesList } from "./-components/categories-list";
import { OverallVision } from "./-components/overall-vision";

export const Route = createFileRoute("/_user/life-design/")({
  staticData: { title: "Vision" },
  component: RouteComponent,
});

// The switch shares everything this page shows.
const VISION_RESOURCE_TYPES = [
  "overallVision",
  "categoryOfLife",
  "bucketListItem",
] as const;

function RouteComponent() {
  const keyManager = useUnlockedKey();
  const [shareWithCoaches, setShareWithCoaches] = useState(false);

  const { data: categories } = useCategoriesOfLife();
  const { data: sharedStatus } = useUserSharedStatus();
  const shareWithCoachMutation = useShareOverallViewWithCoach();
  const { grantCoachAccess, revokeCoachAccess } = useShareWithCoaches(
    keyManager,
    { resourceTypes: VISION_RESOURCE_TYPES },
  );

  const [showShareDialog, setShowShareDialog] = useState(false);

  useEffect(() => {
    if (sharedStatus?.isSharedOverallViewWithCoach !== undefined) {
      setShareWithCoaches(sharedStatus.isSharedOverallViewWithCoach);
    }
  }, [sharedStatus?.isSharedOverallViewWithCoach]);

  const handleShareToggle = (checked: boolean) => {
    if (checked) {
      setShowShareDialog(true);
    } else {
      shareWithCoachMutation.mutate(false, {
        onSuccess: async () => {
          await revokeCoachAccess();
          setShareWithCoaches(false);
          toast.success("Sharing disabled");
        },
        onError: () => {
          toast.error("Failed to update sharing preference");
        },
      });
    }
  };

  const handleConfirmShare = () => {
    shareWithCoachMutation.mutate(true, {
      onSuccess: async () => {
        await grantCoachAccess();
        setShareWithCoaches(true);
        setShowShareDialog(false);
        toast.success("Vision shared with coaches");
      },
      onError: () => {
        toast.error("Failed to share with coaches");
        setShowShareDialog(false);
      },
    });
  };

  const handleCancelShare = () => {
    setShowShareDialog(false);
  };

  return (
    <div className="space-y-6">
      <Card className="shadow-none hover:shadow-none">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-medium text-gray-900">
            Vision
          </h2>
          <div className="flex items-center gap-4">
            <span className="text-base font-medium text-gray-600">
              Share with coaches
            </span>
            <Switch
              checked={shareWithCoaches}
              onCheckedChange={handleShareToggle}
              disabled={shareWithCoachMutation.isPending}
            />
          </div>
        </div>

        <div className="flex min-h-[600px] flex-col items-stretch gap-3 lg:flex-row">
          <CategoriesList
            categories={categories}
            keyManager={keyManager}
            isShared={shareWithCoaches}
          />

          <div className="flex flex-1 flex-col gap-3">
            <OverallVision isShared={shareWithCoaches} />

            <BucketList isShared={shareWithCoaches} />
          </div>
        </div>
      </Card>

      <ConfirmationDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        title="Are you sure you want to share this?"
        description="Please note that all the information from the Vision will be visible to all of your coaches."
        confirmText="Confirm Sharing"
        cancelText="Cancel"
        onConfirm={handleConfirmShare}
        onCancel={handleCancelShare}
        isLoading={shareWithCoachMutation.isPending}
      />
    </div>
  );
}
